import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck, Trash2 } from "lucide-react";
import { useNotifications } from "../data/NotificationsContext";

function levelClass(level: string) {
  if (level === "critical" || level === "error") return "badge-offline";
  if (level === "warn" || level === "warning") return "badge-warn";
  return "badge-idle";
}

function typeLabel(type: string) {
  if (type === "agent_release") return "OTA 发版";
  if (type === "alert") return "告警";
  if (type === "ticket") return "工单";
  if (type === "system") return "系统";
  return type;
}


function formatTime(iso: string) {
  const d = new Date(iso);
  const now = Date.now();
  const diff = now - d.getTime();
  if (diff < 60_000) return "刚刚";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} 分钟前`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} 小时前`;
  return d.toLocaleDateString();
}

export function NotificationBell() {
  const { items, unread, loading, refresh, markRead, markAllRead, remove } =
    useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) {
      document.addEventListener("mousedown", onClick);
      return () => document.removeEventListener("mousedown", onClick);
    }
  }, [open]);

  const preview = items.slice(0, 8);

  return (
    <div className="notif-bell" ref={ref}>
      <button
        type="button"
        className="icon-btn notif-bell-trigger"
        aria-label="通知"
        aria-expanded={open}
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) void refresh();
        }}
      >
        <Bell size={18} />
        {unread > 0 ? (
          <span className="notif-bell-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="notif-popover" role="dialog" aria-label="通知列表">
          <header className="notif-popover-head">
            <strong>通知</strong>
            <div className="row-actions">
              {unread > 0 ? (
                <button
                  type="button"
                  className="btn btn-ghost btn-xs"
                  onClick={() => void markAllRead()}
                >
                  <CheckCheck size={14} />
                  全部已读
                </button>
              ) : null}
            </div>
          </header>
          {loading && preview.length === 0 ? (
            <p className="notif-empty muted">加载中…</p>
          ) : preview.length === 0 ? (
            <p className="notif-empty muted">暂无通知</p>
          ) : (
            <ul className="notif-list">
              {preview.map((n) => (
                <li
                  key={n.id}
                  className={`notif-item${n.read ? "" : " unread"}`}
                >
                  <div className="notif-item-main">
                    <div className="notif-item-title">
                      <span className={`badge ${levelClass(n.level)}`}>
                        {typeLabel(n.type)}
                      </span>
                      <strong>{n.title}</strong>
                    </div>
                    <p>{n.body}</p>
                    <span className="muted">{formatTime(n.createdAt)}</span>
                  </div>
                  <div className="notif-item-actions">
                    {!n.read ? (
                      <button
                        type="button"
                        className="icon-btn icon-btn-sm"
                        title="标为已读"
                        aria-label="标为已读"
                        onClick={() => void markRead(n.id)}
                      >
                        <CheckCheck size={14} />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="icon-btn icon-btn-sm danger"
                      title="删除"
                      aria-label="删除"
                      onClick={() => void remove(n.id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {n.projectId ? (
                    <Link
                      className="notif-item-link"
                      to={`/projects/${n.projectId}`}
                      onClick={() => setOpen(false)}
                    >
                      查看项目
                    </Link>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          <footer className="notif-popover-foot">
            <Link to="/notifications" onClick={() => setOpen(false)}>
              查看全部
            </Link>
          </footer>
        </div>
      ) : null}
    </div>
  );
}
